import { SelectChangeEvent, Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { EmploymentStatus, User } from '@/types/user';

import { getEmploymentStatusOptions } from '@/utils/employment';
import { extractLocalPhoneNumber, isCaymanUser } from '@/utils/user';

import { UpdatePersonalInfo } from '../types';
import { CaymanSelfEmployedFormSection } from './CaymanSelfEmployedFormSection';
import { EmployedFormSection } from './EmployedFormSection';
import { SelfEmploymentFormSection } from './SelfEmployedFormSection';
import { UnemployedFormSection } from './UnemployedFormSection';

import { UpdatePersonalInfoDetailCard } from './UpdateEmploymentDetailsCard.styled';

export const UpdateEmploymentForm = () => {
  const { t } = useTranslate();
  const {
    control,
    watch,
    setValue,
    formState: { errors: visibleErrors },
  } = useFormContext<UpdatePersonalInfo>();
  const user = useAppSelector(selectLoggedInUser) as User;
  const employmentStatus = watch('employmentDetailed.employmentStatus');

  const handleAfterChange = (event: SelectChangeEvent<unknown>) => {
    const newEmploymentStatus = event.target.value as EmploymentStatus;
    if (newEmploymentStatus && newEmploymentStatus !== employmentStatus) {
      setValue('employmentDetailed.employmentDesc', '');

      if (newEmploymentStatus === EmploymentStatus.Employed) {
        setValue(
          'employmentDetailed.riskSectorFlag',
          user.employmentDetailed?.riskSectorFlag,
        );
        setValue(
          'employmentDetailed.isStartup',
          user.employmentDetailed?.isStartup,
        );
      }

      if (
        newEmploymentStatus === EmploymentStatus.SelfEmployed ||
        newEmploymentStatus === EmploymentStatus.Unemployed
      ) {
        const { tenant, phoneNumber } = extractLocalPhoneNumber(
          user.employmentDetailed?.employerPhone || '',
        );

        setValue(
          'employmentDetailed.employerAddress',
          user.employmentDetailed?.employerAddress || '',
        );
        setValue(
          'employmentDetailed.employerName',
          user.employmentDetailed?.employerName || '',
        );
        setValue(
          'employmentDetailed.occupationCode',
          user.employmentDetailed?.occupationCode || '',
        );
        setValue(
          'employmentDetailed.startDate',
          user.employmentDetailed?.startDate || '',
        );
        setValue('employmentDetailed.employerPhone', phoneNumber);
        setValue('employmentDetailed.employerPhoneCode', tenant || '');
      }
    }
  };

  return (
    <UpdatePersonalInfoDetailCard direction={'column'}>
      <Stack direction="column" gap={1} alignItems="stretch">
        <Typography variant="h5">
          {t('updatePersonalInfo.employmentInfoForm.title')}
        </Typography>
        <Divider
          width="100%"
          containerProps={{
            my: 2,
          }}
        />
        <Typography variant="body2Medium">
          {t(
            'updatePersonalInfo.employmentInfoForm.field.employmentStatus.label',
          )}
        </Typography>
        <Select
          options={getEmploymentStatusOptions().map(item => ({
            value: item.value,
            text: t(item.text || ''),
          }))}
          control={control}
          name="employmentDetailed.employmentStatus"
          error={!!visibleErrors.employmentDetailed?.employmentStatus}
          helperText={visibleErrors.employmentDetailed?.occupationCode?.message}
          rules={{
            required: t(
              'updatePersonalInfo.employmentInfoForm.field.employmentStatus.error.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'updatePersonalInfo.employmentInfoForm.field.employmentStatus.placeholder',
          )}
          onSelectChange={event => handleAfterChange(event)}
          useDrawerOnMobile
          withSearchBar
        />
        {employmentStatus === EmploymentStatus.SelfEmployed && (
          <>
            {isCaymanUser(user) ? (
              <CaymanSelfEmployedFormSection visibleErrors={visibleErrors} />
            ) : (
              <SelfEmploymentFormSection visibleErrors={visibleErrors} />
            )}
          </>
        )}
        {employmentStatus === EmploymentStatus.Employed && (
          <EmployedFormSection visibleErrors={visibleErrors} />
        )}
        {employmentStatus === EmploymentStatus.Unemployed && (
          <UnemployedFormSection visibleErrors={visibleErrors} />
        )}
      </Stack>
    </UpdatePersonalInfoDetailCard>
  );
};

export default React.memo(UpdateEmploymentForm);
